// App.js
import React, { useState, useEffect, useContext } from 'react';
import {
  Routes,
  Route,
  Link,
  Navigate,
  useNavigate,
  useLocation
} from 'react-router-dom';
import jsPDF from 'jspdf';
import Register from './Register';
import VerifyEmail from './VerifyEmail';
import MainContent from './MainContent'; 
import Loading from './Loading'; 
import Header from './components/Header';
import Dashboard from './Dashboard';
import AdminFeedback from './AdminFeedback.js';
import ForgotPassword from './ForgotPassword';
import ResetPassword from './ResetPassword';
import AdminEssays from './AdminEssays';
import About from './components/About';
import PrivacyPolicy from './PrivacyPolicy';
import FAQ from './FAQ';
import MyAccount from './MyAccount';
import TestMathRendering from './components/math/TestMathRendering';
import Feedback from './components/Feedback';
import logoImage from './images/logo.png';
import ReferralTerms from './ReferralTerms'; // Import the new component
import AboutUK from './components/About_UK'; // Import UK About component
import FAQ_UK from './FAQ_UK'; // Import UK FAQ component
import IeTandCs from './ie_TandCs';
import UkTandCs from './uk_TandCs';
import TrustworthyAI from './TrustworthyAI'; // Added import for Trustworthy AI page

import {AuthContext} from './AuthContext';  // <-- import from your context
import './styles/App.scss';
import { setupAuthInterceptor } from './utils/authInterceptor';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import LoadingAnecdotes from './components/LoadingAnecdotes';
import flowchart from './images/flowchart.png';
import LoginPage from './components/LoginPage';
import { Analytics } from '@vercel/analytics/react';
import { Desktop } from './landing_page';  // Import the landing page component
import { Desktop as UKDesktop } from './UK_landing_page';  // Import the UK landing page component
import logger from './utils/logger';
import StepsIndicator from './components/StepsIndicator'; // Import StepsIndicator
import StudentSubmissionsList from './components/StudentSubmissionsList'; // Import the new component
import ComprehensionResults from './components/ComprehensionResults'; // Import for the details route
import QuizWorkflow from './components/QuizWorkflow'; // Ensure QuizWorkflow is imported

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
logger.info("API_BASE_URL:", API_BASE_URL);

// Add this function to print multiline text with pagination, test
function printMultilineText(doc, textLines, x, yStart, lineHeight) {
  let y = yStart;
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  
  for (let i = 0; i < textLines.length; i++) {
    // Check if we need a new page
    if (y + lineHeight > pageHeight - margin) {
      doc.addPage();
      y = margin; // Reset y to top margin of new page
    }
    doc.text(textLines[i], x, y);
    y += lineHeight;
  }
  return y; // Return final y position
}

// Add this function to handle HTML content in PDFs
function stripHtml(html) {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

// Function to check if it's a GCSE category
function isGCSECategory(category) {
  return category === 'GCSE_Shakespeare' || category === 'GCSE_19th' || category === 'GCSE_Poetry' || category === 'GCSE_Inspector';
}

// Function to parse feedback line by line
function parseLineByLine(text, category) {
  // Decide which feedback codes to look for:
  let validCodes = '[PCLM]';
  if (isGCSECategory(category)) {
    validCodes = '[ALCM]';
  } else if (category === 'Aiste' || category === 'Grammar Checker' || category.startsWith('Filiocht_') || category === 'IE_Oral') {
    validCodes = '[GCS]';
  } else if (
    category.startsWith('FR_') ||
    category.startsWith('ES_')
  ) {
    validCodes = '[CL]';  // French/Spanish = Communication & Language
  } else if (category.startsWith('DE_')) {
    validCodes = '[CE]';  // German = Content & Expression
  }

  // We'll create a dynamic regex that checks for {X: ...}, 
  // where X is one of the valid codes for this category
  const isSingleBracket = (line) => {
    const pattern = new RegExp(`^\\{${validCodes}:`, 'i');
    return pattern.test(line.trim());
  };

  const lines = text.split('\n');
  const paragraphs = [];
  let currentParagraph = null;

  for (let rawLine of lines) {
    const lineTrim = rawLine.trim();
    if (!lineTrim) continue;

    if (isSingleBracket(lineTrim)) {
      // This line is feedback
      if (currentParagraph) {
        currentParagraph.feedback.push(rawLine);
      }
    } else {
      // Paragraph text or leftover short text
      if (lineTrim.length > 5) {
        if (currentParagraph && currentParagraph.text.trim().length > 0) {
          paragraphs.push(currentParagraph);
          currentParagraph = {
            text: rawLine,
            feedback: []
          };
        } else if (currentParagraph) {
          currentParagraph.text = rawLine;
        } else {
          currentParagraph = {
            text: rawLine,
            feedback: []
          };
        }
      } else {
        // lineTrim <= 5 => leftover short text
        if (!currentParagraph) {
          currentParagraph = { text: rawLine, feedback: [] };
        } else {
          currentParagraph.text += '\n' + rawLine;
        }
      }
    }
  }
  if (currentParagraph && currentParagraph.text.trim().length > 0) {
    paragraphs.push(currentParagraph);
  }
  
  return paragraphs;
}

// Function to strip inline annotations
function stripInlineAnnotations(text) {
  return text.replace(/\{\{[PCLMGSE]:.*?\}\}/g, '').replace(/\\n/g, '\n');
}

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();

  // Pull from AuthContext
  const { 
    isAuthenticated, 
    authToken, 
    storeToken, 
    storeUserData,
    clearToken,
    usageRemaining,
    setUsageRemaining,
    userData
  } = useContext(AuthContext);

  const [step, setStep] = useState(1);
  const [category, setCategory] = useState('');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [imageFiles, setImageFiles] = useState([]);
  const [result, setResult] = useState(null);
  const [currentQuizId, setCurrentQuizId] = useState(null); // For quiz workflow
  const [currentAttemptId, setCurrentAttemptId] = useState(null); // For quiz workflow

  // Clear all state when auth changes
  useEffect(() => {
    if (!isAuthenticated) {
      setStep(1);
      setCategory('');
      setQuestion('');
      setAnswer('');
      setImageFiles([]);
      setResult(null);
      setShortTaskId(null);
      setLinkifyTaskId(null);
      setRawFeedback('');
      setLinkifiedFeedback('');
      setPollingPhase('SHORT');
      setFeedbackText('');
      setFeedbackSubmitted(false);
      setFeedbackCategory('Feedback on the App Functionality');
      
      // Clear persisted task IDs from localStorage
      try {
        localStorage.removeItem('currentShortTaskId');
        localStorage.removeItem('continueEssay');
      } catch (e) {
        logger.error('Failed to clear localStorage:', e);
      }
    }
  }, [isAuthenticated]);

  // For two-task chaining
  const [shortTaskId, setShortTaskId] = useState(() => {
    // Initialize from localStorage if needed for page refreshes
    try {
      return localStorage.getItem('currentShortTaskId') || null;
    } catch (e) {
      logger.error('localStorage not available:', e);
      return null;
    }
  });
  const [linkifyTaskId, setLinkifyTaskId] = useState(null);
  const [rawFeedback, setRawFeedback] = useState("");
  const [linkifiedFeedback, setLinkifiedFeedback] = useState("");
  const [pollingPhase, setPollingPhase] = useState("SHORT");

  // Feedback
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [feedbackCategory, setFeedbackCategory] = useState('Feedback on the App Functionality');

  // UI
  const [isLoading, setIsLoading] = useState(false);

  // For login form
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [error, setError] = useState(null);
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Debug watchers
  useEffect(() => {
    logger.info("rawFeedback updated:", rawFeedback);
  }, [rawFeedback]);

  useEffect(() => {
    logger.info("linkifiedFeedback updated:", linkifiedFeedback);
  }, [linkifiedFeedback]);

  useEffect(() => {
    logger.info("Polling phase updated:", pollingPhase);
  }, [pollingPhase]);

  // Debug effect for step and category changes
  useEffect(() => {
    logger.debug(`AppContent - Step: ${step}, Category: ${category}`);
  }, [step, category]);

  // Helper to determine if we are in any quiz-related view
  const isInQuizWorkflow = () => {
    // If a specific quiz is loaded, or if the category is Biology/Chemistry,
    // assume we are in a quiz-related flow where the standard steps don't apply.
    if (currentQuizId !== null || category === 'Biology' || category === 'Chemistry') {
      return true;
    }
    return false;
  };

  // Step Navigation
  const nextStep = () => setStep(prev => prev + 1);
  const prevStep = () => setStep(prev => Math.max(prev - 1, 1));

  // Handlers for question/category
  const handleCategorySelect = (selectedCategory) => {
    if (selectedCategory === 'Grammar Checker' || selectedCategory === 'FR_Grammar' || selectedCategory === 'ES_Grammar' || selectedCategory === 'DE_Grammar') {
      // For Grammar Checker, skip directly to step 3 (image upload/answer input)
      setCategory(selectedCategory);
      setQuestion("Grammar Check");  // Set a default question
      setStep(3);
    } else {
      // Normal flow for other categories
      setCategory(selectedCategory);
      setStep(2);
    }
  };

  const handleQuestionSubmit = () => {
    if (!question.trim()) return alert('Please enter a question.');
    nextStep();
  };

  const handleAnswerChange = (e) => setAnswer(e.target.value);
  const handleImageUpload = (e) => {
    const newFiles = Array.from(e.target.files);
    setImageFiles(prev => [...prev, ...newFiles]);
  };
  const handleRemoveImage = (index) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Submit images
  const handleSubmitAnswer = async () => {
    if (!answer.trim() && imageFiles.length === 0) {
      return alert('Please provide an answer or upload an image.');
    }

    setIsLoading(true);

    try {
      if (imageFiles.length > 0) {
        const formData = new FormData();
        imageFiles.forEach((file) => formData.append('images', file));
        const imgResponse = await fetch(`${API_BASE_URL}/api/ocr-images`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${authToken}` },
          body: formData
        });

        if (imgResponse.status === 429) {
          alert('You have reached your usage limit. Please try again later.');
          setIsLoading(false);
          return;
        }
        if (!imgResponse.ok) {
          throw new Error('Error uploading images');
        }

        const imgData = await imgResponse.json();
        setAnswer(imgData.response);
        if (imgData.usageRemaining !== undefined) {
          setUsageRemaining(imgData.usageRemaining);
        }
      }
      setIsLoading(false);
      nextStep();
    } catch (error) {
      logger.error(error);
      alert('There was a problem submitting your answer. Please try again.');
      setIsLoading(false);
    }
  };

  // Helper function to map categories to subjects
  const getCategorySubject = (category) => {
    // Map category to subject
    if (category === 'Shakespeare' || category === 'Poetry' || 
        category === 'Comparative' || category === 'Composition' ||
        category.startsWith('GCSE_')) {
      return 'English';
    } else if (category === 'Aiste' || category === 'Grammar Checker' || 
              category === 'IE_Oral' || category.startsWith('Filiocht')) {
      return 'Irish';
    } else if (category.startsWith('FR_')) {
      return 'French';
    } else if (category.startsWith('ES_')) {
      return 'Spanish';
    } else if (category.startsWith('DE_')) {
      return 'German';
    } else if (category.startsWith('MATH_')) {
      return 'Maths';
    } else {
      return 'English'; // Default to English
    }
  };

  // Confirm Answer -> triggers short & linkify tasks
  const handleConfirmAnswer = async (feedbackType, shareWithTeacher, ignoreFadaEnabled, grammarExemption, subject) => {
    if (!answer.trim()) {
      return alert('Please provide an answer.');
    }
    setIsLoading(true);
    
    try {
      logger.info("Making API request to:", `${API_BASE_URL}/api/results`);

      const response = await fetch(`${API_BASE_URL}/api/results`, {
        method: 'POST',
        headers: {
          'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          question,
          answer,
          category,
          feedbackType,
          visible_to_teacher: shareWithTeacher,
          ignore_fada: ignoreFadaEnabled,
          grammar_exemption: grammarExemption,
          subject: subject || getCategorySubject(category) // Use provided subject or derive from category
        })
      });

      logger.info("API Response status:", response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        logger.error('Results Error:', response.status, errorText);
        throw new Error(`Error triggering tasks: ${errorText}`);
      }

      const data = await response.json();
      logger.debug("API Response data:", data);
      
      // Update usage remaining if it's in the response
      if (data.usageRemaining !== undefined) {
        logger.info("Updating usage remaining to:", data.usageRemaining);
        setUsageRemaining(data.usageRemaining);
      }

      // Only check shortTaskId now:
      if (!data.shortTaskId) {
        logger.error("Missing shortTaskId in response:", data);
        throw new Error('Server response missing required shortTaskId');
      }

      setShortTaskId(data.shortTaskId);
      setLinkifyTaskId(data.linkifyTaskId);
      setDbTaskId(data.dbTaskId);
      setPollingPhase("SHORT");

      // Start polling with both IDs
      if (data.linkifyTaskId) {
        pollShortTask(data.shortTaskId, data.linkifyTaskId);
      } else {
        logger.info("Skipping linkify polling for Irish category.");
        // Still do short-task polling:
        pollShortTask(data.shortTaskId, null);
      }
    } catch (error) {
      logger.error("handleConfirmAnswer error:", error);
      alert('There was a problem sending your request: ' + error.message);
      setIsLoading(false);
    }
  };

  // Poll the short task
  const pollShortTask = async (shortId, linkifyId) => {
    logger.info("pollShortTask called with shortId:", shortId, "linkifyId:", linkifyId);
    
    // Track attempt number and use exponential backoff
    if (!window.pollAttempts) {
      window.pollAttempts = { [shortId]: 0 };
    } else if (!window.pollAttempts[shortId]) {
      window.pollAttempts[shortId] = 0;
    }
    
    window.pollAttempts[shortId]++;
    const currentAttempt = window.pollAttempts[shortId];
    
    // Calculate backoff interval - starts at 2s, caps at 8s
    // Base interval doubles every 5 attempts
    const baseInterval = Math.min(2000 * Math.pow(2, Math.floor(currentAttempt / 5)), 8000);
    // Add jitter to avoid thundering herd problem (+/- 20%)
    const jitter = baseInterval * (0.8 + Math.random() * 0.4);
    
    try {
      const statusRes = await fetch(`${API_BASE_URL}/api/results/status/${shortId}`, {
        headers: {
          'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`
        }
      });
      const statusData = await statusRes.json();
      
      // Only log full details periodically to reduce noise
      if (currentAttempt === 1 || currentAttempt % 5 === 0) {
        logger.info("Short task status response:", statusData);
      } else {
        logger.debug(`Short task ${shortId} status: ${statusData.state} (attempt ${currentAttempt})`);
      }

      if (statusData.state === "PENDING" || statusData.state === "STARTED") {
        setTimeout(() => pollShortTask(shortId, linkifyId), jitter);
      } else if (statusData.state === "SUCCESS") {
        logger.info("Short task succeeded:", statusData.result);

        // Update usage remaining if present
        if (statusData.usageRemaining !== undefined) {
          logger.info("Updating usage remaining from short task to:", statusData.usageRemaining);
          setUsageRemaining(statusData.usageRemaining);
        }

        if (statusData.result?.raw_feedback) {
          setRawFeedback(statusData.result.raw_feedback);
          setResult(statusData.result.raw_feedback);
        }

        setIsLoading(false);
        setStep(5);

        // Start linkify polling
        if (linkifyId) {
          startLinkifyPolling(linkifyId);
        } else {
          logger.info("Skipping linkify polling for Irish category.");
        }
        
        // Reset poll attempts for this task
        if (window.pollAttempts && window.pollAttempts[shortId]) {
          delete window.pollAttempts[shortId];
        }
      } else if (statusData.state === "FAILURE") {
        logger.error(`Short task failed: ${statusData.error}`);
        setIsLoading(false);
        alert("There was a problem processing your short task.");
        
        // Reset poll attempts for this task
        if (window.pollAttempts && window.pollAttempts[shortId]) {
          delete window.pollAttempts[shortId];
        }
      }
    } catch (err) {
      logger.error("Error polling short task:", err);
      setTimeout(() => pollShortTask(shortId, linkifyId), jitter);
    }
  };

  // Add new continuous polling function for linkify task
  const startLinkifyPolling = (linkifyId) => {
    logger.info("startLinkifyPolling initiated with linkifyId:", linkifyId);
    
    // Setup polling with exponential backoff
    if (!window.pollAttempts) {
      window.pollAttempts = {};
    }
    window.pollAttempts[linkifyId] = 0;
    
    const pollWithBackoff = async () => {
      window.pollAttempts[linkifyId]++;
      const currentAttempt = window.pollAttempts[linkifyId];
      
      // Calculate backoff interval - starts at 2s, caps at 10s
      const baseInterval = Math.min(2000 * Math.pow(2, Math.floor(currentAttempt / 5)), 10000);
      const jitter = baseInterval * (0.8 + Math.random() * 0.4);
      
      try {
        const statusRes = await fetch(`${API_BASE_URL}/api/results/status/${linkifyId}`, {
          headers: {
            'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`
          }
        });
        const statusData = await statusRes.json();
        
        // Only log details periodically to reduce noise
        if (currentAttempt === 1 || currentAttempt % 5 === 0) {
          logger.info("Linkify polling response:", statusData);
        } else {
          logger.debug(`Linkify task ${linkifyId} status: ${statusData.state} (attempt ${currentAttempt})`);
        }

        // Update usage remaining if present
        if (statusData.usageRemaining !== undefined) {
          logger.info("Updating usage remaining from linkify task to:", statusData.usageRemaining);
          setUsageRemaining(statusData.usageRemaining);
        }

        if (statusData.state === "SUCCESS" && statusData.result?.linkified_feedback) {
          logger.info("Linkify task succeeded with feedback");
          setLinkifiedFeedback(statusData.result.linkified_feedback);
          setResult(statusData.result.linkified_feedback);
          setPollingPhase("DONE");
          
          // Reset poll attempts for this task
          if (window.pollAttempts && window.pollAttempts[linkifyId]) {
            delete window.pollAttempts[linkifyId];
          }
        } else if (statusData.state === "FAILURE") {
          logger.error("Linkify task failed:", statusData.error);
          alert("There was a problem linkifying your text.");
          
          // Reset poll attempts for this task
          if (window.pollAttempts && window.pollAttempts[linkifyId]) {
            delete window.pollAttempts[linkifyId];
          }
        } else {
          // Schedule next poll with backoff
          setTimeout(pollWithBackoff, jitter);
        }
      } catch (err) {
        logger.error("Error in linkify polling:", err);
        
        // Only retry for a reasonable number of times on error
        if (currentAttempt < 30) {
          setTimeout(pollWithBackoff, jitter);
        } else {
          logger.error("Giving up on linkify polling after too many errors");
          if (window.pollAttempts && window.pollAttempts[linkifyId]) {
            delete window.pollAttempts[linkifyId];
          }
        }
      }
    };
    
    // Start polling
    pollWithBackoff();
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      logger.info("Login request URL:", `${API_BASE_URL}/api/login`);
      const response = await fetch(`${API_BASE_URL}/api/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          identifier: username,
          password: password
        })
      });

      const data = await response.json();

      if (response.status === 403 && data.needsVerification) {
        setShowResendVerification(true);
        setUserEmail(data.email);
        setError("Your email is not verified. Click below to resend verification email.");
        return;
      }

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      const token = data.token;
      storeToken(token);
      setUsageRemaining(data.usageRemaining);
      setError(null);
      setIsLoggedIn(true);
    } catch (error) {
      setError(error.message);
      logger.error('Login error:', error);
    }
  };
  // Reset
  const handleResetApp = () => {
    setStep(1);
    setCategory('');
    setQuestion('');
    setAnswer('');
    setImageFiles([]);
    setResult(null);
    setRawFeedback("");
    setLinkifiedFeedback("");
    setSelectedBranch(null);
    setFeedbackSubmitted(false);
    setFeedbackText('');
    setFeedbackCategory('Feedback on the App Functionality');
    
    // Clear quiz-related state
    setCurrentQuizId(null);
    setCurrentAttemptId(null);
    
    // Clear persisted task IDs
    try {
      localStorage.removeItem('currentShortTaskId');
      localStorage.removeItem('continueEssay');
    } catch (e) {
      logger.error('Failed to clear localStorage during reset:', e);
    }
  };

  // Save PDF
  const handleSavePDF = () => {
    if (!result) return;
    
    try {
      // Find the content container in the DOM
      const contentElement = document.querySelector('.content-wrapper');
      if (!contentElement) {
        alert("Couldn't find the content to save. Please try again.");
        return;
      }
      
      // Create a clone of the element to not affect what's displayed
      const clone = contentElement.cloneNode(true);
      
      // Remove elements we don't want in the PDF (buttons, feedback form, etc.)
      const buttonsContainer = clone.querySelector('.action-buttons-container');
      if (buttonsContainer) buttonsContainer.remove();
      
      const feedbackSection = clone.querySelector('.feedback-section');
      if (feedbackSection) feedbackSection.remove();
      
      const reviseButton = clone.querySelector('.result-revise-button-container');
      if (reviseButton) reviseButton.remove();
      
      const usageRemaining = clone.querySelector('.usage-remaining');
      if (usageRemaining) usageRemaining.remove();
      
      // Set up a temporary container for the clone - using a wider width
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.width = '1000px'; // Wider width to match screen view
      document.body.appendChild(tempContainer);
      tempContainer.appendChild(clone);
      
      // Apply necessary styling to make sure it looks good in the PDF
      const tempStyle = document.createElement('style');
      tempStyle.textContent = `
        .content-wrapper {
          background-color: white;
          padding: 20px;
          font-family: Arial, sans-serif;
          color: black;
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
        }
        .rating-buttons, button {
          display: none !important;
        }
        .feedback-item {
          page-break-inside: avoid;
          width: 100%;
        }
        .paragraph-container {
          page-break-inside: avoid;
          margin-bottom: 15px;
          width: 100%;
          display: block !important;
        }
        .paragraph-text {
          width: 100% !important;
          max-width: none !important;
          padding-right: 20px;
          font-size: 14px;
          line-height: 1.5;
        }
        .feedback-column {
          width: 100% !important;
          max-width: none !important;
          margin-top: 10px;
          padding-left: 20px;
          border-left: 1px solid #eee;
        }
        .analysis-container {
          width: 100% !important;
          max-width: none !important;
        }
        .feedback-columns {
          width: 100% !important;
          max-width: none !important;
          display: block !important;
          column-count: 1 !important;
          columns: 1 !important;
        }
        .question-box {
          width: 100% !important;
          max-width: none !important;
          margin-bottom: 20px;
        }
        /* Make text larger and more readable */
        p, div {
          font-size: 14px;
          line-height: 1.5;
        }
        /* Force reset any column or grid layouts */
        [class*="col-"], .row, .container, .container-fluid {
          width: 100% !important;
          max-width: none !important;
          flex: none !important;
          display: block !important;
        }
        /* Make all text containers take full width */
        .paragraph-text > div, .feedback-item > div {
          width: 100% !important;
          max-width: none !important;
        }
      `;
      tempContainer.appendChild(tempStyle);
      
      // Break the two-column layout if it exists
      const paragraphContainers = clone.querySelectorAll('.paragraph-container');
      paragraphContainers.forEach(container => {
        // Force paragraph containers to be block elements
        container.style.display = 'block';
        container.style.width = '100%';
        
        // Make paragraph text full width
        const paragraphText = container.querySelector('.paragraph-text');
        if (paragraphText) {
          paragraphText.style.width = '70%';
          paragraphText.style.maxWidth = 'none';
          paragraphText.style.fontSize = '14px';
          paragraphText.style.lineHeight = '1.5';
        }
        
        // Style feedback column
        const feedbackColumn = container.querySelector('.feedback-column');
        if (feedbackColumn) {
          feedbackColumn.style.width = '30%';
          feedbackColumn.style.maxWidth = 'none';
          feedbackColumn.style.marginTop = '10px';
          feedbackColumn.style.paddingLeft = '20px';
          feedbackColumn.style.borderLeft = '1px solid #eee';
        }
      });
      
      // Ensure question box is full width
      const questionBox = clone.querySelector('.question-box');
      if (questionBox) {
        questionBox.style.width = '100%';
        questionBox.style.maxWidth = 'none';
      }
      
      // Add page heading and footer manually, since we're removing the other UI
      const header = document.createElement('h1');
      header.style.textAlign = 'center';
      header.style.fontSize = '22px';
      header.style.fontFamily = 'Arial, sans-serif';
      header.style.marginBottom = '25px';
      header.textContent = 'Your Essay Analysis';
      clone.insertBefore(header, clone.firstChild);
      
      // Remove the logo in the HTML content since we'll add it programmatically to each PDF page
      // This prevents duplication on the first page
      
      const footer = document.createElement('div');
      footer.style.textAlign = 'center';
      footer.style.fontSize = '10px';
      footer.style.color = '#777';
      footer.style.marginTop = '30px';
      footer.style.paddingTop = '10px';
      footer.style.borderTop = '1px solid #eee';
      footer.style.fontFamily = 'Arial, sans-serif';
      footer.textContent = `Generated by Pulc on ${new Date().toLocaleDateString()}`;
      clone.appendChild(footer);
      clone.style.paddingBottom = "50px";
      // Import html2canvas
      import('html2canvas').then(async (html2canvas) => {
        // First, give the browser a moment to render with the new styles
        setTimeout(async () => {
          try {
            const canvas = await html2canvas.default(clone, {
              scale: 1.5, // Balanced scale for quality and file size
              useCORS: true,
              logging: false,
              allowTaint: true,
              backgroundColor: "white",
              width: tempContainer.offsetWidth,
              height: tempContainer.scrollHeight,
              windowWidth: 1000 // Match container width
            });
            
            // 1) Set up jsPDF
            const pdf = new jsPDF({
              orientation: 'portrait',
              unit: 'mm',
              format: 'a4'
            });

            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            // 2) Decide margins (in mm)
            const marginX = 10; // Add horizontal margin
            const marginY = 10;
            const usableWidth = pageWidth - (2 * marginX);
            const usableHeight = pageHeight - (2 * marginY);

            // 3) Calculate how many pixels fit on one page at our target dimensions
            const ratio = canvas.width / usableWidth;
            const pageHeightInPx = usableHeight * ratio;
            
            // 4) Calculate how many pages we need
            const pagesCount = Math.ceil(canvas.height / pageHeightInPx);
            
            // 5) For each page, create a new canvas with just that portion
            for (let i = 0; i < pagesCount; i++) {
              // If not the first page, add a new page to the PDF
              if (i > 0) {
                pdf.addPage();
              }
              
              // Create a new canvas for this page section
              const pageCanvas = document.createElement('canvas');
              const pageCtx = pageCanvas.getContext('2d');
              
              // Set dimensions for this page's canvas
              pageCanvas.width = canvas.width;
              
              // Calculate the height for this page (might be less for the last page)
              const sourceY = i * pageHeightInPx;
              const remainingHeight = canvas.height - sourceY;
              const pageCanvasHeight = Math.min(pageHeightInPx, remainingHeight);
              pageCanvas.height = pageCanvasHeight;
              
              // Draw the appropriate portion of the original canvas onto this one
              pageCtx.drawImage(
                canvas, 
                0, sourceY,               // Start coordinates in source
                canvas.width, pageCanvasHeight,  // Dimensions in source
                0, 0,                     // Start coordinates in destination
                canvas.width, pageCanvasHeight   // Dimensions in destination
              );
              
              // Convert this page canvas to an image for the PDF
              const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.95);
              
              // Add this page image to the PDF
              pdf.addImage(
                pageImgData, 
                'JPEG', 
                marginX, 
                marginY, 
                usableWidth, 
                (pageCanvasHeight / canvas.width) * usableWidth
              );
              
              // Add logo to every page
              if (i === 0) {
                // Create an Image object for the logo
                const logoElement = new Image();
                logoElement.src = logoImage;
                
                // Add the logo once it's loaded
                logoElement.onload = () => {
                  // Calculate logo dimensions while maintaining aspect ratio
                  const logoWidth = 20; // in mm
                  const logoHeight = (logoWidth / logoElement.width) * logoElement.height;
                  
                  // Add logo to the top right of each page
                  for (let j = 1; j <= pagesCount; j++) {
                    pdf.setPage(j);
                    pdf.addImage(
                      logoElement, 
                      'PNG', 
                      pageWidth - marginX - logoWidth, // Position from right
                      marginY, // Position from top
                      logoWidth, // Width in mm
                      logoHeight // Height in mm
                    );
                  }
                  
                  // -- Page numbering logic -- //
                  const totalPages = pdf.internal.getNumberOfPages();
                  for (let j = 1; j <= totalPages; j++) {
                    pdf.setPage(j);
                    pdf.setFontSize(8);
                    pdf.setTextColor(100);
                    pdf.text(
                      `Page ${j} of ${totalPages}`,
                      pageWidth / 2,
                      pageHeight - 5,
                      { align: 'center' }
                    );
                  }
                  
                  // Save PDF after logo is added to all pages
                  pdf.save('essay_analysis.pdf');
                };
              }
            }

          } catch (err) {
            logger.error('Error in HTML2Canvas:', err);
            alert('There was a problem generating the PDF. Please try again.');
          } finally {
            // Clean up
            document.body.removeChild(tempContainer);
          }
        }, 100); // small delay to let styles apply
      }).catch(error => {
        logger.error('Error loading html2canvas:', error);
        alert('There was a problem generating the PDF. Please try again.');
        document.body.removeChild(tempContainer);
      });
    } catch (error) {
      logger.error('Error generating PDF:', error);
      alert('There was a problem generating the PDF. Please try again.');
    }
  };

  // Feedback Submit
  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    logger.info("Submitting feedback:", {  // Debug log
      feedbackText,
      feedbackCategory,
      question,
      answer,
      result
    });
    
    if (!feedbackText.trim()) {
      alert("Please enter feedback before submitting.");
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`
        },
        body: JSON.stringify({
          feedback: feedbackText,        // user_feedback in backend
          category: feedbackCategory,    // category in backend
          question: question,            // question_text in backend
          answer: answer,                // answer_text in backend
          result: result                 // app_feedback in backend
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('Feedback Error:', response.status, errorText);
        logger.error('Request details:', {  // Debug log
          feedbackText,
          feedbackCategory,
          question,
          answer,
          result,
          status: response.status
        });
        throw new Error('Error submitting feedback');
      }

      const data = await response.json();
      alert(data.message);
      setFeedbackSubmitted(true);
    } catch (error) {
      logger.error(error);
      alert("Failed to submit feedback. Please try again.");
    }
  };

  // Get username from token when auth changes
  useEffect(() => {
    if (authToken) {
      try {
        const payload = JSON.parse(atob(authToken.split('.')[1]));
        setUsername(payload.sub);
      } catch (e) {
        logger.error('Error decoding token:', e);
      }
    }
  }, [authToken]);

  // Reset to step 1 when user logs in
  useEffect(() => {
    if (isAuthenticated) {
      setStep(1);
      setSelectedBranch(null);
    }
  }, [isAuthenticated]);

  // Add this function to handle image files
  const handleImageFiles = (files) => {
    setImageFiles(files);
  };

  // Set up the auth interceptor when the app starts
  setupAuthInterceptor();

  const handleResendVerification = async () => {
    try {
      await fetch(`${API_BASE_URL}/api/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: userEmail })
      });
      alert("Verification email sent! Please check your inbox.");
      setShowResendVerification(false);
    } catch (error) {
      alert("Failed to resend verification email");
    }
  };

  // Move the continueEssay check to a separate function
  const checkForContinuedEssay = () => {
    const continueEssay = localStorage.getItem('continueEssay');
    if (continueEssay) {
      const essayData = JSON.parse(continueEssay);
      setQuestion(essayData.question);
      setAnswer(essayData.answer);
      setCategory(essayData.category || 'Shakespeare');
      setStep(4); // Always go to step 4
      localStorage.removeItem('continueEssay');
      return true;
    }
    return false;
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
        logger.info('Attempting Google login with credential:', credentialResponse);
        logger.info('API URL:', `${API_BASE_URL}/api/google-login`);
        
        const response = await fetch(`${API_BASE_URL}/api/google-login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({
                credential: credentialResponse.credential
            })
        });

        logger.info('Response received:', response);
        const data = await response.json();
        logger.info('Response data:', data);

        if (!response.ok) {
            throw new Error(data.error || 'Google login failed');
        }

        storeToken(data.token);
        setUsageRemaining(data.usageRemaining);
        setError(null);
        setIsLoggedIn(true);
    } catch (error) {
        logger.error('Detailed Google login error:', {
            message: error.message,
            stack: error.stack,
            response: error.response
        });
        setError(error.message);
    }
  };

  const handleGoogleError = () => {
    setError('Google login failed. Please try again.');
  };

  // Add this console log to verify the client ID is loaded
  logger.info("Google Client ID:", process.env.REACT_APP_GOOGLE_CLIENT_ID);

  useEffect(() => {
    logger.info('Environment variables:', {
      clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      apiBaseUrl: process.env.REACT_APP_API_BASE_URL
    });
  }, []);

  const [savedEssayData, setSavedEssayData] = useState(null);

  const handleContinueEssay = (essayData) => {
    logger.info('Continuing essay with data:', essayData);
    setSavedEssayData(essayData);
    setQuestion(essayData.question || '');
    setAnswer(essayData.answer || '');
    setCategory(essayData.category || 'Shakespeare');
    if (essayData.skipGrading && essayData.result) {
      setResult(essayData.result);
      setShortTaskId(essayData.task_id); 
    }
    if (essayData.essayId) {
      // setEssayId(essayData.essayId); // If you add essayId state to App.js
    }
    if (essayData.startAtStep) {
      setStep(essayData.startAtStep);
    } else {
      setStep(4); // Default to confirm answer step
    }
    // Navigate to main app page if not already there
    if (location.pathname !== '/') {
      navigate('/');
    }
  };

  // Update localStorage when shortTaskId changes
  useEffect(() => {
    if (shortTaskId) {
      try {
        localStorage.setItem('currentShortTaskId', shortTaskId);
      } catch (e) {
        logger.error('Failed to save to localStorage:', e);
      }
    }
  }, [shortTaskId]);

  const [dbTaskId, setDbTaskId] = useState(null);

  useEffect(() => {
    if (savedEssayData) {
      logger.info('Processing savedEssayData:', {
        ...savedEssayData,
        resultLength: savedEssayData.result ? savedEssayData.result.length : 0,
        isMathCategory: savedEssayData.category === 'MATH_Concepts' || savedEssayData.category === 'MATH_Applications'
      });
      
      setQuestion(savedEssayData.question);
      setAnswer(savedEssayData.answer);
      setCategory(savedEssayData.category);
      
      if (savedEssayData.skipGrading && savedEssayData.result) {
        logger.info('Setting result and preparing to show feedback view');
        setResult(savedEssayData.result);
        setDbTaskId(savedEssayData.task_id);
        
        // Check if this is math feedback
        if (savedEssayData.isMathFeedback) {
          logger.info('Directing to math workflow (step 2) for feedback');
          // For math feedback, go to step 2 which will render MathWorkflow
          // The category (MATH_Concepts or MATH_Applications) will determine the proper display
          setStep(2);
        } else {
          logger.info('Directing to regular feedback view (step 5)');
          // For regular feedback, continue to step 5 (ResultAndRevision)
          setStep(5);
        }
      }
    }
  }, [savedEssayData]);

  const [selectedBranch, setSelectedBranch] = useState(null);

  // Determine if the StepsIndicator should be shown
  const isMathFlow = typeof category === 'string' && category.startsWith('MATH_');
  const isComprehensionFlow = typeof category === 'string' && category === 'Comprehension';
  const isQuizFlow = typeof category === 'string' && (category === 'Biology' || category === 'Chemistry');
  const isCustomFlow = typeof category === 'string' && category === 'Custom';
  const showStepsIndicator = isAuthenticated && 
                             step >= 1 && 
                             step <= 4 && 
                             !isMathFlow && 
                             !isComprehensionFlow && 
                             !isQuizFlow &&
                             !isCustomFlow;

  // Determine if the user is from the UK
  const isUKUser = userData?.country === 'UK';
  logger.debug('AppContent - isUKUser:', isUKUser, 'based on country:', userData?.country);

  const handleViewQuizAttempt = (quizId, attemptId, quizSubject) => {
    logger.info(`Navigating to view quiz attempt: quizId=${quizId}, attemptId=${attemptId}, subject=${quizSubject}`);
    setCurrentQuizId(quizId);
    setCurrentAttemptId(attemptId);
    // The subject for the quiz workflow should be 'Biology' or 'Chemistry'
    // We need to ensure quizSubject is correctly passed or derived.
    // For now, we'll rely on it being passed if available, or derive it.
    const workflowSubject = getQuizSubjectFromCategory(quizSubject) || quizSubject; 
    setCategory(workflowSubject); 
    setStep(1); // This will trigger QuizWorkflow via MainContent
    navigate('/'); // Navigate to the main page where MainContent renders QuizWorkflow
  };

  // Define getCategorySubject at this level if not already available globally
  // or import it if it's in a utils file.
  // For now, let's assume it's similar to the one in Dashboard.js for simplicity
  const getQuizSubjectFromCategory = (cat) => {
    if (!cat) return null;
    if (cat === 'Biology' || cat === 'Chemistry') return cat;
    // Add more mappings if quiz categories expand beyond Biology/Chemistry
    logger.warn(`Quiz subject mapping not found for category: ${cat}`);
    return null; // Fallback or more specific handling
  };

  // Function to clear quiz state
  const clearQuizState = () => {
    setCurrentQuizId(null);
    setCurrentAttemptId(null);
  };

  return (
    <>
      {isLoading && <LoadingAnecdotes isUKUser={isUKUser} />}
      <Routes>
        <Route path="/register" element={<Register />} />
        <Route path="/verify-email/:token" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/math-debug" element={<TestMathRendering />} />
        <Route path="/uk/faq" element={<FAQ_UK />} />
        <Route path="/dashboard" element={
          isAuthenticated ? (
            <Dashboard 
              authToken={authToken} 
              onContinueEssay={handleContinueEssay}
              onViewQuizAttempt={handleViewQuizAttempt}
            />
          ) : (
            <Navigate to="/login" />
          )
        } />
        <Route 
          path="/admin/feedback" 
          element={
            isAuthenticated && (username === "charles" || username === "cillian") ? 
            <AdminFeedback authToken={authToken} /> : 
            <Navigate to="/" />
          } 
        />
        <Route 
          path="/admin/essays" 
          element={
            isAuthenticated && (username === "charles" || username === "cillian") ? 
            <AdminEssays 
              authToken={authToken}
              onContinueEssay={handleContinueEssay}
            />: 
            <Navigate to="/" />
          } 
        />
        <Route path="/about" element={<About />} />
        <Route path="/uk/about" element={<AboutUK />} />
        <Route 
            path="/feedback" 
            element={
              isAuthenticated ? (
                <Feedback />
              ) : (
                <Navigate to="/" />
              )
            } 
          />
        <Route 
            path="/account" 
            element={
              isAuthenticated ? (
                <MyAccount />
              ) : (
                <Navigate to="/" />
              )
            } 
          />
        <Route path="/login" element={
          <LoginPage
            handleLogin={handleLogin}
            error={error}
            setError={setError}
            username={username}
            setUsername={setUsername}
            password={password}
            setPassword={setPassword}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            showResendVerification={showResendVerification}
            handleResendVerification={handleResendVerification}
            storeToken={storeToken}
            storeUserData={storeUserData}
            navigate={navigate}
          />
        } />
        
        {/* UK-specific landing page route - accessed via Vercel rewrites for pulc.co.uk domain */}
        <Route path="/uk" element={<UKDesktop />} />
        
        <Route path="/" element={
          isAuthenticated ? (
            <div className="app-container">
              <Header onNewEssay={handleResetApp} />

              {/* Conditionally render StepsIndicator */} 
              {showStepsIndicator && (
                <StepsIndicator 
                  currentStep={Math.floor(step)} 
                  setStep={setStep}
                  category={category}
                  question={question}
                />
              )}
              
              <MainContent
                step={Math.floor(step)}
                setStep={setStep}
                category={category}
                setCategory={setCategory}
                question={question}
                setQuestion={setQuestion}
                usageRemaining={usageRemaining}
                setUsageRemaining={setUsageRemaining}
                handleQuestionSubmit={handleQuestionSubmit}
                prevStep={prevStep}
                nextStep={nextStep}
                imageFiles={imageFiles}
                handleImageUpload={handleImageUpload}
                handleRemoveImage={handleRemoveImage}
                answer={answer}
                setAnswer={setAnswer}
                handleAnswerChange={handleAnswerChange}
                result={result}
                setResult={setResult}
                handleResetApp={handleResetApp}
                feedbackSubmitted={feedbackSubmitted}
                handleFeedbackSubmit={handleFeedbackSubmit}
                feedbackCategory={feedbackCategory}
                setFeedbackCategory={setFeedbackCategory}
                feedbackText={feedbackText}
                setFeedbackText={setFeedbackText}
                handleSavePDF={handleSavePDF}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                authToken={authToken}
                handleImageFiles={handleImageFiles}
                API_BASE_URL={API_BASE_URL}
                onConfirmAnswer={handleConfirmAnswer}
                userData={userData}
                savedEssayData={savedEssayData}
                clearSavedEssayData={() => setSavedEssayData(null)}
                shortTaskId={shortTaskId}
                setShortTaskId={setShortTaskId}
                dbTaskId={dbTaskId}
                selectedBranch={selectedBranch}
                setSelectedBranch={setSelectedBranch}
                currentQuizId={currentQuizId}
                currentAttemptId={currentAttemptId}
                clearQuizState={clearQuizState}
              />
            </div>
          ) : (
            <Desktop />  // Show landing page for non-authenticated users
          )
        } />
        {/* Add route for student's submission list */} 
        <Route 
          path="/my-submissions" 
          element={authToken ? <StudentSubmissionsList /> : <Navigate to="/login" />} 
        />
        {/* Add route for viewing a specific submission detail */} 
        <Route 
          path="/submission/:submissionId" 
          element={authToken ? <ComprehensionResults /> : <Navigate to="/login" />} 
        />
        {/* Add route for teacher comprehensions management */}
        <Route 
          path="/teacher/comprehensions" 
          element={authToken ? <ComprehensionResults /> : <Navigate to="/login" />} 
        />
        <Route path="/referral-terms" element={<ReferralTerms />} />
        <Route path="/ie-tandcs" element={<IeTandCs />} />
        <Route path="/uk-tandcs" element={<UkTandCs />} />
        <Route path="/trustworthy-ai" element={<TrustworthyAI />} />
      </Routes>
      <Analytics />
    </>
  );
}

function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <AppContent />
    </GoogleOAuthProvider>
  );
}

export default App;
